import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import useFormatNumber from '../../../i18n/useFormatNumber'
import { es } from 'date-fns/locale'
import { format } from 'date-fns'
import { capitalize } from 'lodash'

import { Alert, Box, Typography, Link, Chip } from '@mui/material'

import { ReactComponent as Clock } from '../../../assets/icons/clock.svg'
import { ReactComponent as MapPin } from '../../../assets/icons/map-pin.svg'
import { ReactComponent as Warning } from '../../../assets/icons/warning.svg'
import { ReactComponent as Success } from '../../../assets/icons/success.svg'
import { ReactComponent as Phone } from '../../../assets/icons/phone.svg'
import { ReactComponent as EnvelopeSimple } from '../../../assets/icons/envelope-simple.svg'
import { ReactComponent as CalendarBlank } from '../../../assets/icons/calendar-blank.svg'
import { ReactComponent as Comments } from '../../../assets/icons/comments.svg'
import { ReactComponent as Question } from '../../../assets/icons/question.svg'
import { ReactComponent as CirclesFour } from '../../../assets/icons/circles-four.svg'
import { ReactComponent as PackageClose } from '../../../assets/icons/package-close.svg'
import { ReactComponent as Weight } from '../../../assets/icons/weight.svg'

import {
  ADDRESS_STATES,
  CUSTOM_SELECT_REASON,
  CUSTOM_SELECT_TYPE,
  STEPS,
  ADDRESS_SOURCES,
} from '../../../common/constants'
import {
  shippingSend1FromPropTypes,
  shippingSend2ToPropTypes,
  shippingSend3ShipmentPropTypes,
} from '../../../common/propTypes'
import {
  formErrorsPropType,
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../../common/formikPropTypes'
import { nullablePropType } from '../../../common/propTypes'
import { getErrorText } from '../../../common/formProps'
import { isOriginNewAddress } from '../../../context/ShippingSend/formatSubmitData'

import useTranslations from '../../../i18n/useTranslations'

import FormLayout from './FormLayout'
import useStyles from './styles'
import CompleteInfoItem from '../../components/listItem/CompleteInfoItem'
import InfoItem from '../../components/listItem/InfoItem'
import Divider from '../../components/divider'
import TooltipButton from '../../components/tooltipButton'

import { generateRandomKey } from '../../../utils'
import { getFullAddressTextFromValues } from './common'
import { useState } from 'react'
import { PICKUP_OPTIONS } from '../../../context/ShippingSend/Step1Origin/Pickup'
import NormativeModal from '../../modals/normative'
import { useUserContext } from '../../../auth/UserProvider'

// TODO dado que esta función también se utiliza en el archivo
// `formatSubmitData', considerar moverla a su propio archivo o al archivo
// de utilidades de turno más cercano a ambos componentes.
export const createFullAddress = values =>
  values.addressSource !== 'newAddress'
    ? values.selectedAddress.shippingAddress.address
    : `${values.address.address}`
        .concat(
          values.address.stair !== ''
            ? `, escalera ${values.address.stair}`
            : '',
        )
        .concat(
          values.address.floor !== '' ? `, piso ${values.address.floor}` : '',
        )
        .concat(
          values.address.letter !== ''
            ? `, letra ${values.address.letter}`
            : '',
        )

// TODO: refactorizar para usar el mismo schema que en el paso 1
export const createDestinFullAddress = values =>
  values.addressState !== ADDRESS_STATES.NEW &&
  values.addressState !== ADDRESS_STATES.AT_PICKUP_POINT
    ? values.address.autocompleteAddress
    : `${values.address.address}`
        .concat(
          values.address.stair !== ''
            ? `, escalera ${values.address.stair}`
            : '',
        )
        .concat(
          values.address.floor !== '' ? `, piso ${values.address.floor}` : '',
        )
        .concat(
          values.address.letter !== ''
            ? `, letra ${values.address.letter}`
            : '',
        )

const Address = ({ fieldErrors, values }) => {
  const t = useTranslations()
  const intl = useIntl()
  const classes = useStyles()

  const fullAddress = createDestinFullAddress(values)

  const isPickupPoint = values.addressState === ADDRESS_STATES.AT_PICKUP_POINT

  const address =
    values.addressState !== ADDRESS_STATES.NEW &&
    values.addressState !== ADDRESS_STATES.AT_PICKUP_POINT
      ? {
          name: values.address.name,
          address: values.address.address,
          postalCode: values.address.postalCode,
          city: values.address.city,
          country: values.address.country,
        }
      : {
          name: values.address.name,
          address: fullAddress,
          postalCode: values.address.postalCode,
          city: values.address.city,
          country: values.address.country.code,
        }

  return (
    <CompleteInfoItem
      icon={<MapPin />}
      title={
        <span className={fieldErrors?.name ? classes.addressError : undefined}>
          {/* NOTE: El copy 'Punto de recogida' aparece si es una entrega en PUDO */}
          {isPickupPoint ? 'Punto CTT Express:' : null}{' '}
          {isPickupPoint ? values.address.contactName : values.address.name}
        </span>
      }
      subtitle={getFullAddressTextFromValues(
        address,
        fieldErrors,
        classes.addressError,
        intl,
        t,
      )}
      error={
        fieldErrors?.name ||
        fieldErrors?.address ||
        fieldErrors?.postalCode ||
        fieldErrors?.country
          ? t('error:form:step')
          : null
      }
    />
  )
}

Address.propTypes = {
  fieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend2ToPropTypes).isRequired,
  ),
  values: makeValuesPropType(shippingSend2ToPropTypes).isRequired,
}

// NOTE este componente se ha creado expresamente para mostrar los datos del
// primer paso del formulario (Origen) debido al refactor que se ha hecho sobre
// el mismo.
// REVIEW la lógica interna de este componente no está revisada ni optimizada,
// se ha escrito rápidamente para poder dar por concluido el refactor del paso 1.
// TODO considerar mejorar la lógica para obtener la dirección con todos sus campos
// concatenados (escalera, piso y letra).
// TODO cuando se haya abordado el refactor del paso 2, considerar utilizar este
// componente para mostrar la información de ambos pasos.
const OriginAddress = ({ fieldErrors, values }) => {
  const t = useTranslations()
  const intl = useIntl()
  const classes = useStyles()

  const isOriginPickupPoint =
    values.addressSource === ADDRESS_SOURCES.pickUpPoint
  const fullAddress = createFullAddress(values)

  const address =
    values.addressSource !== 'newAddress'
      ? {
          name: values.selectedAddress.shippingAddress.name,
          address: values.selectedAddress.shippingAddress.address,
          postalCode: values.selectedAddress.shippingAddress.postalCode,
          city: values.selectedAddress.shippingAddress.city,
          country: values.selectedAddress.shippingAddress.country,
        }
      : {
          name: values.address.name,
          address: fullAddress,
          postalCode: values.address.postalCode,
          city: values.address.city,
          country: values.address.country.code,
        }
  return (
    <CompleteInfoItem
      icon={<MapPin />}
      title={
        <span className={fieldErrors?.name ? classes.addressError : undefined}>
          {isOriginPickupPoint ? t('pickupPoint:title') : address.name}
        </span>
      }
      subtitle={
        isOriginPickupPoint
          ? t('pickupPoint:subtitle')
          : getFullAddressTextFromValues(
              address,
              fieldErrors,
              classes.addressError,
              intl,
              t,
            )
      }
      error={
        fieldErrors?.name ||
        fieldErrors?.address ||
        fieldErrors?.postalCode ||
        fieldErrors?.country
          ? t('error:form:step')
          : null
      }
    />
  )
}

OriginAddress.propTypes = {
  fieldErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  values: PropTypes.object,
}

const Step4Summary = ({
  shipmentWasCreated,
  values,
  fieldErrors,
  formErrors,
  onClickStep,
  aditionalServices,
  shippingWeightDeclared,
}) => {
  const t = useTranslations()
  const formatNumber = useFormatNumber()
  const classes = useStyles()
  const [openModal, setOpenModal] = useState(false)
  const user = useUserContext()
  const showOriginFirstDivider =
    (Boolean(values.from.address.phone) ||
      Boolean(values.from.address.email)) &&
    (Boolean(values.from.pickup) || Boolean(values.from.comments))
  const showOriginSecondDivider =
    Boolean(values.from.pickup) && Boolean(values.from.comments)
  const showDestinationFirstDivider =
    (Boolean(values.to.address.phone) || Boolean(values.to.address.email)) &&
    (Boolean(values.to.contactPerson) ||
      Boolean(values.to.department) ||
      Boolean(values.to.comments))
  const showDestinationSecondDivider =
    (Boolean(values.to.contactPerson) || Boolean(values.to.department)) &&
    Boolean(values.to.comments)

  const isPickupPoint =
    values.to.addressState === ADDRESS_STATES.AT_PICKUP_POINT

  const isOriginPickupPoint =
    values.from.addressSource === ADDRESS_SOURCES.pickUpPoint

  const getPickupInfoItemSubtitle = () => {
    if (values.from.pickupSource === PICKUP_OPTIONS.requestLater) {
      return (
        <span>
          {t('action:unscheduled-date')}
          <br />
          <small className={classes.smallText}>
            {t('message:unscheduled-date-subtitle')}
          </small>
        </span>
      )
    } else {
      return t('shipping-send-form:summary:pickup', {
        weekDay: capitalize(
          format(new Date(values.from.pickup.split('/')[0]), 'eeee', {
            locale: es,
          }),
        ),
        minDate: new Date(values.from.pickup.split('/')[0]),
        maxDate: new Date(values.from.pickup.split('/')[1]),
      })
    }
  }

  const formatName = fileName => {
    const maxLength = 40
    if (fileName.length <= maxLength) {
      return fileName
    } else {
      const extensionIndex = fileName.lastIndexOf('.')
      const nameWithoutExtension = fileName.substring(0, extensionIndex)
      const extension = fileName.substring(extensionIndex)
      const truncatedName =
        nameWithoutExtension.substring(0, maxLength - extension.length) + '...'
      return truncatedName + extension
    }
  }

  const calculateTotalPrice = serviceType => {
    if (!serviceType) return '- €'

    const additionalWeightSize = serviceType.additionals
      ? serviceType.additionals
          .filter(
            additional => additional.additional_type_code === 'WEIGHT_SIZE',
          )
          .reduce(
            (acc, additional) =>
              acc + (parseFloat(additional.additional_amount) || 0),
            0,
          )
      : 0

    const totalPrice = additionalWeightSize
    return `${formatNumber(totalPrice)} €`
  }

  const allFieldsComplete = () => {
    const filter = values.shipment.packages.filter(
      ({ height, length, weight, width }) =>
        height && length && weight && width,
    )
    return filter?.length
  }
  return (
    <>
      <NormativeModal onClose={() => setOpenModal(false)} isOpen={openModal} />
      <FormLayout>
        <Box className={classes.summaryGrid}>
          <Box className={classes.section}>
            <Typography
              variant="h1"
              className={clsx('secondaryTitle', 'decorator', classes.title)}
            >
              {t('noun:origin-data')}
            </Typography>

            <OriginAddress
              fieldErrors={fieldErrors.from}
              values={values.from}
            />
            {!isOriginPickupPoint && (
              <Typography variant="body2" className={classes.helper}>
                {t('message:outside-origin')}
              </Typography>
            )}
          </Box>

          <Box className={classes.section}>
            <Typography
              variant="h1"
              className={clsx('secondaryTitle', 'decorator', classes.title)}
            >
              {t('noun:destination-data')}
            </Typography>

            <Address fieldErrors={fieldErrors.to} values={values.to} />
            {values?.shipment?.serviceType?.code === 'C10-CTT 10h' ? (
              <>
                <Typography variant="body2" className={classes.helper}>
                  {t('message:outside-destination-km-10h')}
                </Typography>
                <Link
                  href={process.env.REACT_APP_SUPPLEMENTS_URL}
                  target={'_blank'}
                  className={classes.helperLink}
                >
                  {t('message:outside-destination-km-10h-mi')}
                </Link>
              </>
            ) : null}
          </Box>
        </Box>

        <Box className={classes.summaryGrid}>
          <Box>
            <Divider light hideEnd />
          </Box>
          <Box>
            <Divider light hideStart />
          </Box>
        </Box>

        <Box className={classes.summaryGrid}>
          <Box className={classes.section}>
            <InfoItem
              icon={<Phone />}
              title={t('noun:phone')}
              subtitle={
                isOriginNewAddress(values.from.addressSource) ||
                values.from.addressSource === 'pickUpPoint'
                  ? values.from.address.phone
                    ? `${values.from.address.phonePrefix} ${values.from.address.phone}`
                    : '-'
                  : values.from.selectedAddress.shippingAddress.phone
              }
              error={getErrorText(fieldErrors.from?.address?.phone, t)}
            />

            {Boolean(values.from.address.email) && (
              <InfoItem
                icon={<EnvelopeSimple />}
                title={t('noun:email')}
                subtitle={values.from.address.email}
                error={getErrorText(fieldErrors.from?.address?.email, t)}
              />
            )}
            {showOriginFirstDivider && (
              <Box py={2}>
                <Divider variant="middle" light />
              </Box>
            )}
            {!isOriginPickupPoint && (
              <InfoItem
                icon={<CalendarBlank />}
                title={t('noun:pick-up-date')}
                subtitle={getPickupInfoItemSubtitle()}
                error={getErrorText(fieldErrors.from?.pickUpTimeSlot, t)}
                className={
                  values.from.pickupSource === PICKUP_OPTIONS.requestLater
                    ? classes.warning
                    : null
                }
              />
            )}
            {Boolean(values.from.pickUpTimeSlot) && (
              <InfoItem
                icon={<CalendarBlank />}
                title={t('noun:pick-up-date')}
                subtitle={t('shipping-send-form:summary:pickup', {
                  minDate: new Date(values.from.pickup.split('/')[0]),
                  maxDate: new Date(values.from.pickup.split('/')[1]),
                })}
                error={getErrorText(fieldErrors.from?.pickUpTimeSlot, t)}
              />
            )}
            {showOriginSecondDivider && (
              <Box py={2}>
                <Divider variant="middle" light />
              </Box>
            )}

            {Boolean(values.from.comments) && (
              <InfoItem
                icon={<Comments />}
                title={t('noun:comments')}
                subtitle={values.from.comments}
                error={getErrorText(fieldErrors.from?.comments, t)}
              />
            )}

            {shipmentWasCreated ? null : (
              <Box mt={2}>
                <Link
                  component="button"
                  onClick={() => onClickStep(STEPS.FROM)}
                >
                  {t('action:change-data')}
                </Link>
              </Box>
            )}
          </Box>

          <Box className={classes.section}>
            <InfoItem
              icon={<Phone />}
              title={t('noun:phone')}
              subtitle={`${values.to.address.phonePrefix ?? ''} ${
                values.to.address.phone
              }`}
              error={getErrorText(fieldErrors.to?.address?.phone, t)}
            />
            {/* NOTE: Se debe mostrar el horario de entrega en PUDO. También aparecerá debajo de los datos un Alert avisando de la entrega en PUDO */}
            {isPickupPoint ? (
              <>
                <InfoItem
                  icon={<Clock />}
                  title={t('noun:timetable')}
                  subtitle={values.to.address.schedule?.map(schedule => (
                    <p
                      key={`${schedule.dayName}:${schedule.openingHours}`}
                      className="radio-extraInfoData font-h4"
                    >
                      {schedule.dayName}: {schedule.openingHours}
                    </p>
                  ))}
                  error={getErrorText(fieldErrors.to?.address?.phone, t)}
                  className={classes.scheduleInfoItem}
                />
                <Alert severity="info" className={classes.pickupPointAlert}>
                  <Typography variant="body2">
                    {t('message:pickup-point-contact-data')}
                  </Typography>
                </Alert>
              </>
            ) : null}
            {Boolean(values.to.address.email) && (
              <InfoItem
                icon={<EnvelopeSimple />}
                title={t('noun:email')}
                subtitle={values.to.address.email}
                error={getErrorText(fieldErrors.to?.address?.email, t)}
              />
            )}
            {showDestinationFirstDivider && (
              <Box py={2}>
                <Divider variant="middle" light />
              </Box>
            )}
            {Boolean(values.to.contactPerson) && (
              <InfoItem
                icon={<Question />}
                title={t('noun:ask-about')}
                subtitle={values.to.contactPerson}
                error={getErrorText(fieldErrors.to?.contactPerson, t)}
              />
            )}

            {Boolean(values.to.department) && (
              <InfoItem
                icon={<CirclesFour />}
                title={t('noun:department')}
                subtitle={values.to.department}
                error={getErrorText(fieldErrors.to?.department, t)}
              />
            )}
            {showDestinationSecondDivider && (
              <Box py={2}>
                <Divider variant="middle" light />
              </Box>
            )}
            {Boolean(values.to.comments) && (
              <InfoItem
                icon={<Comments />}
                title={t('noun:comments')}
                subtitle={values.to.comments}
                error={getErrorText(fieldErrors.to?.comments, t)}
              />
            )}

            {shipmentWasCreated ? null : (
              <Box mt={2}>
                <Link component="button" onClick={() => onClickStep(STEPS.TO)}>
                  {t('action:change-data')}
                </Link>
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.summaryGrid}>
          <Box className={classes.section}>
            <Box className={classes.tooltipBox}>
              <Typography
                variant="h1"
                className={clsx('secondaryTitle', 'decorator', classes.title)}
              >
                {t('noun:shipment-detail')}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              pt={1}
            >
              {values.shipment.referenceNumber ? (
                <Chip
                  className={
                    fieldErrors.shipment?.referenceNumber ? 'primary' : 'purple'
                  }
                  label={
                    <>
                      {t('noun:reference')}
                      {': '}
                      <strong>{values.shipment.referenceNumber}</strong>
                    </>
                  }
                  avatar={<Success />}
                />
              ) : null}
              {values.shipment.serviceType ? (
                <Chip
                  className={
                    fieldErrors.shipment?.serviceType?.code
                      ? 'primary'
                      : 'purple'
                  }
                  label={
                    <>
                      {t('noun:contracted-service')}
                      {': '}
                      <strong>
                        {values?.shipment?.serviceType?.code?.split('-')[2]
                          ? values?.shipment?.serviceType?.code?.split('-')[2]
                          : values?.shipment?.serviceType?.code?.split('-')[1]}
                      </strong>
                    </>
                  }
                  avatar={<MapPin />}
                />
              ) : null}
              <Chip
                className={
                  fieldErrors.shipment?.packages ? 'primary' : 'purple'
                }
                label={
                  <>
                    {t('noun:total-packages')}
                    {': '}
                    <strong>{values.shipment.packages.length}</strong>
                  </>
                }
                avatar={<PackageClose />}
              />
              <Chip
                className={
                  fieldErrors.shipment?.packages ? 'primary' : 'purple'
                }
                label={
                  <>
                    {t('noun:reported weight')}
                    {': '}
                    <strong>
                      {t('placeholder:kilograms', {
                        number: formatNumber(shippingWeightDeclared),
                      })}
                    </strong>
                  </>
                }
                avatar={<Weight />}
              />
              {user?.isFinancial && allFieldsComplete() ? (
                <Chip
                  className={
                    fieldErrors.shipment?.packages ? 'primary' : 'purple'
                  }
                  label={
                    <>
                      {t('noun:base-amount')}
                      {': '}
                      <strong>
                        {values?.shipment?.serviceType?.transportPrice
                          ? `${formatNumber(
                              values?.shipment?.serviceType?.transportPrice,
                            )} €`
                          : '- €'}
                      </strong>
                    </>
                  }
                  avatar={<Success />}
                />
              ) : null}
              {user?.isFinancial &&
              allFieldsComplete() &&
              !isOriginPickupPoint &&
              calculateTotalPrice(values?.shipment?.serviceType) !==
                '0,00 €' ? (
                <Chip
                  className={
                    fieldErrors.shipment?.packages ? 'primary' : 'purple'
                  }
                  label={
                    <>
                      {t('noun:weight-extra-amount')}
                      {': '}
                      <strong>
                        {calculateTotalPrice(values?.shipment?.serviceType)}
                      </strong>
                    </>
                  }
                  avatar={<Warning />}
                />
              ) : null}
            </Box>

            {fieldErrors.shipment?.referenceNumber ||
            fieldErrors.shipment?.packages ? (
              <Alert severity="error" className={classes.alert}>
                {t('error:form:step')}
              </Alert>
            ) : null}

            {shipmentWasCreated ? null : (
              <Box mt={3}>
                <Link
                  component="button"
                  onClick={() => onClickStep(STEPS.SHIPMENT)}
                >
                  {t('action:change-data')}
                </Link>
              </Box>
            )}
            
            <Typography variant="body2" className={classes.asteriskTxt} style={{marginTop: 15}}>
              {t('shipping-send-form:summary-service-price')}
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography
              variant="h1"
              className={clsx('secondaryTitle', 'decorator', classes.title)}
            >
              {t('noun:additional-services')}
            </Typography>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              pt={1}
            >
              {/* TODO: provisional values until definition */}
              {aditionalServices ? (
                <>
                  {values.shipment?.additionals.withRefund ? (
                    <Chip
                      className={
                        fieldErrors.shipment?.additionals?.withRefund
                          ? 'primary'
                          : 'green'
                      }
                      avatar={<Success />}
                      label={
                        <>
                          {t('noun:refund')}{' '}
                          <strong>
                            {formatNumber(
                              values.shipment?.additionals?.refundAmount,
                            )}
                            {t('field:money-suffix')}
                          </strong>
                        </>
                      }
                    />
                  ) : null}

                  {values.shipment.additionals?.insuranceMerchandise ? (
                    <Chip
                      className={
                        fieldErrors.shipment?.additionals?.insuranceMerchandise
                          ? 'primary'
                          : 'green'
                      }
                      avatar={<Success />}
                      label={
                        <>
                          {t('noun:insurance-merchandise')}{' '}
                          <strong>
                            {formatNumber(
                              values.shipment?.additionals?.insuranceValue,
                            )}
                            {t('field:money-suffix')}
                          </strong>
                        </>
                      }
                    />
                  ) : null}

                  {values.shipment?.additionals?.withReturn ? (
                    <Chip
                      className={
                        fieldErrors?.shipment?.additionals?.withReturn
                          ? 'primary'
                          : 'green'
                      }
                      avatar={<Success />}
                      label={<>{t('service:with-return')} </>}
                    />
                  ) : null}

                  {values.shipment?.additionals?.scanClientSlip ? (
                    <Chip
                      className={
                        fieldErrors?.shipment?.additionals?.insuranceMerchandise
                          ? 'primary'
                          : 'green'
                      }
                      avatar={<Success />}
                      label={<>{t('service:scan-client-slip')} </>}
                    />
                  ) : null}
                </>
              ) : (
                <Typography variant="body2" className={classes.emptyTxt}>
                  {t('noun:additional-services-empty')}
                </Typography>
              )}
            </Box>

            {fieldErrors.shipment?.additionals?.declaredValue ||
            fieldErrors.shipment?.additionals?.verifyCode ||
            fieldErrors.shipment?.additionals?.refundAmount ? (
              <Alert severity="error" className={classes.alert}>
                {t('error:form:step')}
              </Alert>
            ) : null}

            {shipmentWasCreated ? null : (
              <Box mt={3}>
                <Link
                  component="button"
                  onClick={() => onClickStep(STEPS.SHIPMENT)}
                >
                  {t('action:change-data')}
                </Link>
              </Box>
            )}
          </Box>
        </Box>
        {values?.shipment?.hasCustom?.custom ? (
          <>
            <Typography
              variant="h1"
              className={clsx('secondaryTitle', 'decorator')}
            >
              {t('noun:commercial-bill')}
            </Typography>
            <Box className={classes.summaryGrid}>
              <Box className={classes.section}>
                <Typography variant="h2" className={clsx('secondaryTitle')}>
                  {t('noun:sender')}
                </Typography>

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  pt={1}
                >
                  <Typography
                    component="li"
                    variant="body2"
                    className={classes.emptyTxt}
                  >
                    {`${t('shipping-send-form:customs:form:type')}: ${t(
                      CUSTOM_SELECT_TYPE.find(
                        ({ id }) => id === values?.customs?.senderType,
                      )?.text,
                    )}`}
                  </Typography>
                  {values?.customs?.senderType === 'PERSONAL_NATIONAL' ? (
                    <>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t('shipping-send-form:customs:form:dni')}: ${
                          values?.customs?.senderInfo?.dni
                        }`}
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t('shipping-send-form:customs:form:reason')}: ${t(
                          CUSTOM_SELECT_REASON.find(
                            ({ id }) =>
                              id === values?.customs?.senderInfo?.reason,
                          )?.text,
                        )}`}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t(
                          'shipping-send-form:customs:form:company-name',
                        )}: ${values?.customs?.senderInfo?.companyName}`}
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${
                          (values?.to?.address?.country?.code ??
                            values?.to?.address?.country !== 'ES') &&
                          (values?.from?.address?.country?.code ??
                            values?.from?.address?.country) !== 'ES'
                            ? t('shipping-send-form:customs:form:prosecutor')
                            : t('shipping-send-form:customs:form:prosecutor-es')
                        }: ${values?.customs?.senderInfo?.nifIva}`}
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t('shipping-send-form:customs:form:reason')}: ${t(
                          CUSTOM_SELECT_REASON.find(
                            ({ id }) =>
                              id === values?.customs?.senderInfo?.reason,
                          )?.text,
                        )}`}
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t('shipping-send-form:customs:form:bill')}: ${
                          values?.customs?.senderInfo?.bill
                        }`}
                      </Typography>
                    </>
                  )}
                </Box>

                {shipmentWasCreated ? null : (
                  <Box mt={3}>
                    <Link
                      component="button"
                      onClick={() => onClickStep(STEPS.CUSTOMS)}
                    >
                      {t('action:change-data')}
                    </Link>
                  </Box>
                )}
              </Box>
              <Box className={classes.section}>
                <Typography variant="h2" className={clsx('secondaryTitle')}>
                  {t('noun:receptor')}
                </Typography>

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  pt={1}
                >
                  <Typography
                    component="li"
                    variant="body2"
                    className={classes.emptyTxt}
                  >
                    {`${t('shipping-send-form:customs:form:type')}: ${t(
                      CUSTOM_SELECT_TYPE.find(
                        ({ id }) => id === values?.customs?.receptorType,
                      )?.text,
                    )}`}
                  </Typography>
                  {values?.customs?.receptorType === 'PERSONAL_NATIONAL' ? (
                    <>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t('shipping-send-form:customs:form:dni')}: ${
                          values?.customs?.receptorInfo?.dni
                        }`}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${t(
                          'shipping-send-form:customs:form:company-name',
                        )}: ${values?.customs?.receptorInfo?.companyName}`}
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        className={classes.emptyTxt}
                      >
                        {`${
                          (values?.to?.address?.country?.code ??
                            values?.to?.address?.country !== 'ES') &&
                          (values?.from?.address?.country?.code ??
                            values?.from?.address?.country) !== 'ES'
                            ? t('shipping-send-form:customs:form:prosecutor')
                            : t('shipping-send-form:customs:form:prosecutor-es')
                        }: ${values?.customs?.receptorInfo?.nifIva}`}
                      </Typography>
                    </>
                  )}
                </Box>

                {shipmentWasCreated ? null : (
                  <Box mt={3}>
                    <Link
                      component="button"
                      onClick={() => onClickStep(STEPS.CUSTOMS)}
                    >
                      {t('action:change-data')}
                    </Link>
                  </Box>
                )}
              </Box>
            </Box>

            <Typography variant="h2" className={clsx('secondaryTitle')}>
              {t('noun:inventory')}
            </Typography>
            <Box className={classes.summaryGrid}>
              {values?.customs?.inventory.map((element, index) => (
                <Box key={generateRandomKey()}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    pt={1}
                  >
                    <Typography variant="h4" className={clsx('secondaryTitle')}>
                      {t('shipping-send-form:customs:inventory-custom-number', {
                        number: index + 1,
                      })}
                    </Typography>

                    <Typography
                      component="li"
                      variant="body2"
                      className={classes.emptyTxt}
                    >
                      {`${t('shipping-send-form:customs:form:category')}: ${
                        element?.category?.commodity_type_name
                      }`}
                    </Typography>

                    <Typography
                      component="li"
                      variant="body2"
                      className={classes.emptyTxt}
                    >
                      {`${t('shipping-send-form:customs:form:place')}: ${
                        element?.place?.country_ES
                      }`}
                    </Typography>

                    <Typography
                      component="li"
                      variant="body2"
                      className={classes.emptyTxt}
                    >
                      {`${t(
                        'shipping-send-form:customs:form:description-summary',
                      )}: ${element?.description}`}
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      className={classes.emptyTxt}
                    >
                      {`${t('shipping-send-form:customs:form:quantity')}: ${
                        element?.quantity
                      }`}
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      className={classes.emptyTxt}
                    >
                      {`${t('shipping-send-form:customs:form:price')}: ${
                        element?.price
                      }€`}
                    </Typography>
                  </Box>

                  {shipmentWasCreated ? null : (
                    <Box mt={3}>
                      <Link
                        component="button"
                        onClick={() => onClickStep(STEPS.CUSTOMS)}
                      >
                        {t('action:change-data')}
                      </Link>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <Box className={classes.section}>
              <Typography variant="h2" className={clsx('secondaryTitle')}>
                {t('noun:normative')}
              </Typography>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                pt={1}
              >
                <Typography
                  component="li"
                  variant="body2"
                  className={classes.emptyTxt}
                >
                  {t('noun:normative-1')}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  className={classes.emptyTxt}
                >
                  {t('noun:normative-2')}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  className={classes.emptyTxt}
                >
                  {t('noun:normative-3')}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  className={classes.emptyTxt}
                >
                  {t('noun:normative-4')}
                </Typography>
              </Box>

              {shipmentWasCreated ? null : (
                <Box mt={3}>
                  <Link component="button" onClick={() => setOpenModal(true)}>
                    {t('action:see-normative')}
                  </Link>
                </Box>
              )}
            </Box>

            {values?.customs?.additionalDocumentation?.length ? (
              <>
                <Typography
                  variant="h1"
                  className={clsx('secondaryTitle', 'decorator')}
                >
                  {t('noun:additional-documentation')}
                </Typography>

                <Box className={classes.summaryGrid}>
                  <Box className={classes.section}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      pt={1}
                    >
                      {values?.customs?.additionalDocumentation.map(
                        ({ file }, index) => (
                          <Typography
                            key={generateRandomKey()}
                            variant="body2"
                            className={classes.emptyTxt}
                          >
                            {t('noun:document', {
                              number: index + 1,
                              serial: formatName(file.name),
                            })}
                          </Typography>
                        ),
                      )}
                    </Box>

                    {shipmentWasCreated ? null : (
                      <Box mt={3}>
                        <Link
                          component="button"
                          onClick={() => onClickStep(STEPS.CUSTOMS)}
                        >
                          {t('action:change-data')}
                        </Link>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            ) : null}
          </>
        ) : null}

        {formErrors ? (
          <Box
            className={classes.section}
            maxWidth={560}
            margin="auto"
            display="flex"
            justifyContent="center"
          >
            {formErrors?.map(({ key, values }) => (
              <Alert key={key} severity="error">
                {t(key, values)}
              </Alert>
            ))}
          </Box>
        ) : null}
      </FormLayout>
    </>
  )
}

Step4Summary.propTypes = {
  shipmentWasCreated: PropTypes.bool,
  values: PropTypes.shape({
    from: makeValuesPropType(shippingSend1FromPropTypes).isRequired,
    to: Address.propTypes.values,
    shipment: makeValuesPropType(shippingSend3ShipmentPropTypes).isRequired,
  }).isRequired,

  fieldErrors: PropTypes.shape({
    from: nullablePropType(
      makeFieldErrorsPropType(shippingSend1FromPropTypes).isRequired,
    ),
    to: Address.propTypes.fieldErrors,
    shipment: nullablePropType(
      makeFieldErrorsPropType(shippingSend3ShipmentPropTypes).isRequired,
    ),
  }).isRequired,

  formErrors: nullablePropType(formErrorsPropType.isRequired),
  onClickStep: PropTypes.func.isRequired,
  shippingWeightDeclared: PropTypes.number.isRequired,
  //REVIEW
  aditionalServices: PropTypes.any,
}

export default Step4Summary
